/**
 * Psychotherapy note CRUD helpers (author-scoped DocumentReference).
 */

import type { MedplumClient } from '@medplum/core';
import type {
  DocumentReference,
  Encounter,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes';
import {
  APPOINTMENT_TAG_SYSTEM,
  DOCUMENT_TYPE_SYSTEM,
} from '@unify/fhir-model/constants';
import { practitionerPreferredReference } from './loadPractitionerPreferredLabels';
import {
  buildPsychotherapyNote,
  getAuthorRef,
  getDocRefAppointmentId,
  getPsychotherapyNoteText,
  isPsychotherapyNote,
  setPsychotherapyNoteText,
} from '@unify/fhir-model/resources/document-reference';

export interface PsychotherapyNoteRow {
  id: string;
  appointmentId?: string;
  authorDisplay: string;
  authorRef?: string;
  date: string;
  preview: string;
  text: string;
  /** When content is application/pdf (historical import). */
  isPdf: boolean;
  pdfUrl?: string;
  resource: DocumentReference;
}

function authorDisplay(doc: DocumentReference): string {
  const author = doc.author?.[0];
  return author?.display?.trim() || author?.reference || 'Provider';
}

function previewText(text: string, max = 120): string {
  const trimmed = text.replace(/\s+/g, ' ').trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max - 1)}…`;
}

function isPdfAttachment(doc: DocumentReference): boolean {
  const ct = doc.content?.[0]?.attachment?.contentType ?? '';
  return ct.startsWith('application/pdf');
}

export function toPsychotherapyNoteRow(doc: DocumentReference): PsychotherapyNoteRow | null {
  if (!doc.id || !isPsychotherapyNote(doc)) return null;
  const pdf = isPdfAttachment(doc);
  const text = pdf ? '' : getPsychotherapyNoteText(doc);
  const title = doc.content?.[0]?.attachment?.title || doc.description || 'Psychotherapy note';
  return {
    id: doc.id,
    appointmentId: getDocRefAppointmentId(doc),
    authorDisplay: authorDisplay(doc),
    authorRef: getAuthorRef(doc)?.reference,
    date: doc.date ?? doc.meta?.lastUpdated ?? '',
    preview: pdf ? title : previewText(text) || '(Empty)',
    text,
    isPdf: pdf,
    pdfUrl: pdf ? doc.content?.[0]?.attachment?.url : undefined,
    resource: doc,
  };
}

/** Find the current user's psychotherapy note for an appointment (if any). */
export async function findOwnPsychotherapyNoteForAppointment(
  medplum: MedplumClient,
  appointmentId: string,
  authorPractitionerId: string,
): Promise<DocumentReference | undefined> {
  medplum.invalidateSearches('DocumentReference');
  const results = await medplum.searchResources('DocumentReference', {
    type: `${DOCUMENT_TYPE_SYSTEM}|psychotherapy-note`,
    '_tag': `${APPOINTMENT_TAG_SYSTEM}|${appointmentId}`,
    author: `Practitioner/${authorPractitionerId}`,
    _count: '1',
  });
  return results.find(isPsychotherapyNote);
}

/** All psychotherapy notes for an appointment visible to the current user (own + supervisee). */
export async function searchPsychotherapyNotesForAppointment(
  medplum: MedplumClient,
  appointmentId: string,
): Promise<DocumentReference[]> {
  medplum.invalidateSearches('DocumentReference');
  const results = await medplum.searchResources('DocumentReference', {
    type: `${DOCUMENT_TYPE_SYSTEM}|psychotherapy-note`,
    '_tag': `${APPOINTMENT_TAG_SYSTEM}|${appointmentId}`,
    _count: '50',
    _sort: '-date',
  });
  return results.filter(isPsychotherapyNote);
}

/** All psychotherapy notes for a patient visible to the current user. */
export async function searchPsychotherapyNotesForPatient(
  medplum: MedplumClient,
  patientId: string,
): Promise<DocumentReference[]> {
  medplum.invalidateSearches('DocumentReference');
  const results = await medplum.searchResources('DocumentReference', {
    type: `${DOCUMENT_TYPE_SYSTEM}|psychotherapy-note`,
    subject: `Patient/${patientId}`,
    _count: '200',
    _sort: '-date',
  });
  return results.filter(isPsychotherapyNote);
}

export async function savePsychotherapyNote(input: {
  medplum: MedplumClient;
  existing?: DocumentReference | null;
  patientRef: Reference<Patient>;
  author: Practitioner;
  appointmentId: string;
  text: string;
  encounterRef?: Reference<Encounter>;
}): Promise<DocumentReference> {
  const { medplum, existing, patientRef, author, appointmentId, text, encounterRef } = input;
  const authorRef = practitionerPreferredReference(author);

  if (existing?.id) {
    const updated: DocumentReference = { ...existing };
    setPsychotherapyNoteText(updated, text);
    updated.date = new Date().toISOString();
    return medplum.updateResource(updated);
  }

  const created = buildPsychotherapyNote({
    patientRef,
    authorRef,
    appointmentId,
    text,
    encounterRef,
  });
  return medplum.createResource(created);
}
